import "./../Styles/BankReconciliation.css";

import {

    Fragment,
    useState,
    useEffect,
    useMemo

} from "react";

import {

    useNavigate

} from "react-router-dom";

import {

    Landmark,
    CircleDollarSign,
    CheckCircle2,
    Clock3,
    XCircle,
    Wallet,
    Search,
    RefreshCcw,
    ArrowLeft

} from "lucide-react";

import {

    Trash2,

    CircleAlert

} from "lucide-react";


import CountUp from "react-countup";

import { toast } from "react-toastify";

import api from "../api";

import LedgerEntryModal
from "../Components/Ledger/LedgerEntryModal";


export default function BankReconciliation(){

    const navigate = useNavigate();


    const [

        cheques,
        setCheques

    ] = useState([]);


const [

    ledgerEntries,

    setLedgerEntries

] = useState([]);


    const [

        loading,
        setLoading

    ] = useState(true);

    const [

        search,
        setSearch

    ] = useState("");

    const [

        selectedMonth,
        setSelectedMonth

    ] = useState(() => {

        const today = new Date();

        return `${today.getFullYear()}-${String(
            today.getMonth()+1
        ).padStart(2,"0")}`;

    });

    const [

        statusFilter,
        setStatusFilter

    ] = useState("ALL");

const [

    showLedgerModal,

    setShowLedgerModal

] = useState(false);

const [

    expandedRow,

    setExpandedRow

] = useState(null);

const [

    editingEntry,

    setEditingEntry

] = useState(null);


const [

    confirmModal,

    setConfirmModal

] = useState({

    open:false,

    type:null,

    row:null

});


/* ==========================================
   LOAD DATA
========================================== */

async function loadLedger(){

    try{

        setLoading(true);

        const [

            chequeData,

            ledgerData

        ] = await Promise.all([

            api.getCheques(),

            api.getBankLedger()

        ]);

        setCheques(chequeData);

        setLedgerEntries(ledgerData);

    }

    catch(err){

        console.error(err);

        toast.error(

            "Unable to load bank ledger."

        );

    }

    finally{

        setLoading(false);

    }

}

useEffect(()=>{

    loadLedger();

},[]);


/* ==========================================
   FILTER CHEQUES
========================================== */

const filteredCheques = useMemo(()=>{

    return cheques

    .filter((cheque)=>{

        if(!cheque.date_posted)
            return false;

        const month = `${

            new Date(
                cheque.date_posted
            ).getFullYear()

        }-${String(

            new Date(
                cheque.date_posted
            ).getMonth()+1

        ).padStart(2,"0")}`;

        return month===selectedMonth;

    })

    .filter((cheque)=>{

        if(statusFilter==="ALL")
            return true;

        return cheque.status===statusFilter;

    })

    .filter((cheque)=>{

        const s =
            search.toLowerCase();

        return(

            cheque.cheque_number
            ?.toString()
            .includes(s)

            ||

            cheque.payer
            ?.toLowerCase()
            .includes(s)

            ||

            cheque.payee
            ?.toLowerCase()
            .includes(s)

        );

    })
.sort(

    (a,b)=>

    new Date(b.date_posted)

    -

    new Date(a.date_posted)

);

},[

    cheques,
    search,
    selectedMonth,
    statusFilter

]);


/* ==========================================
   MANUAL LEDGER ROWS
========================================== */

const manualRows = ledgerEntries.map(entry => ({

    id: entry.id,

    source: entry.transaction_source,

    date: entry.entry_date,

    entryNumber: entry.entry_number,

    category: entry.category,

    type: entry.entry_type,

    description: entry.description,

    reference: entry.reference,

    externalReference: entry.external_reference,

    notes: entry.notes,

    createdBy: entry.created_by,

    createdAt: entry.created_at,

    updatedAt: entry.updated_at,

    debit: Number(entry.debit),

    credit: Number(entry.credit)

}));


/* ==========================================
   CHEQUE ROWS
========================================== */

const chequeRows = filteredCheques

.filter(c=>

    c.status==="Unpresented"

    ||

    c.status==="Deposited"

    ||

    c.status==="Cleared"

    ||

    c.status==="Cancelled"

)

.map(c=>({

    source:"CHEQUE",

    date:c.date_posted,

    entryNumber:c.cheque_number,

    category:"CHEQUE",

    type:c.status,

    description:

        `${c.payer} → ${c.payee}`,

    reference:c.cheque_number,

debit:

    c.status==="Cancelled"

        ? Number(c.amount)

        : 0,

credit:

    c.status!=="Cancelled"

        ? Number(c.amount)

        : 0

}));


/* ==========================================
   MERGED LEDGER
========================================== */

const chronologicalLedger = [

    ...manualRows,

    ...chequeRows

].sort((a, b) => {

    const dateDiff =
        new Date(a.date) -
        new Date(b.date);

    if (dateDiff !== 0)
        return dateDiff;

    return String(a.entryNumber)
        .localeCompare(
            String(b.entryNumber),
            undefined,
            {
                numeric: true
            }
        );

});


/* ==========================================
   CALCULATE RUNNING BALANCE
========================================== */

let runningBalance = 0;

const calculatedLedger = chronologicalLedger.map(row => {

    runningBalance +=

        row.credit -

        row.debit;

    return {

        ...row,

        runningBalance

    };

});


/* ==========================================
   DISPLAY NEWEST FIRST
========================================== */

const ledger =

    [...calculatedLedger]

    .reverse();


/* ==========================================
   CURRENT BANK BALANCE
========================================== */

const bankBalance =

    ledger.length

    ?

    ledger[0].runningBalance

    :

    0;

/* ==========================================
   SUMMARY VALUES
========================================== */

const depositedValue =

    filteredCheques

    .filter(

        c=>c.status==="Deposited"

    )

    .reduce(

        (sum,c)=>

        sum+

        Number(c.amount),

        0

    );


const clearedValue =

    filteredCheques

    .filter(

        c=>c.status==="Cleared"

    )

    .reduce(

        (sum,c)=>

        sum+

        Number(c.amount),

        0

    );


const outstandingValue =

    filteredCheques

    .filter(

        c=>c.status==="Unpresented"

    )

    .reduce(

        (sum,c)=>

        sum+

        Number(c.amount),

        0

    );


const cancelledValue =

    filteredCheques

    .filter(

        c=>c.status==="Cancelled"

    )

    .reduce(

        (sum,c)=>

        sum+

        Number(c.amount),

        0

    );


const totalTransactions =

    ledger.length;



function handleEditEntry(row){

    setConfirmModal({

        open:true,

        type:"edit",

        row

    });

}


function handleDeleteEntry(row){

    setConfirmModal({

        open:true,

        type:"delete",

        row

    });

}


async function handleConfirm(){

    const row = confirmModal.row;

    if(!row) return;

    if(confirmModal.type==="edit"){

        setEditingEntry(row);

        setShowLedgerModal(true);

    }

    if(confirmModal.type==="delete"){

        try{

            await api.deleteBankLedgerEntry(row.id);

            toast.success(

                "Ledger entry deleted."

            );

            setExpandedRow(null);

            loadLedger();

        }

        catch(err){

            console.error(err);

            toast.error(

                "Unable to delete ledger entry."

            );

        }

    }

    setConfirmModal({

        open:false,

        type:null,

        row:null

    });

}




return (

<div className="reconciliation-page">

<button

    className="back-btn"

    onClick={()=>

        navigate("/cheque-tracker")

    }

>

    <ArrowLeft size={18}/>

    Back to Cheque Tracker

</button>

    {/* HERO */}

    <div className="reconciliation-hero">

        <div className="hero-left">


            <div className="hero-pill">

                <Landmark size={15}/>

                BANK LEDGER

            </div>

            <h1>

                Current

                <span> Bank Balance</span>

            </h1>

            <p>
Automatically calculated from all
manual ledger entries and every
issued cheque.

Unpresented cheques immediately
reduce the ledger balance.
Only cancelled cheques reverse
their original ledger entry.

            </p>

            <div className="hero-balance">

                $

                <CountUp

                    end={bankBalance}

                    separator=","

                    duration={1.5}

                    decimals={2}

                />

            </div>

        </div>

<div className="hero-right">

<button

    className="primary-btn"

    onClick={()=>

        setShowLedgerModal(true)

    }

>

    <CircleDollarSign size={18}/>

    New Ledger Entry

</button>

    <button
        className="secondary-btn"
    >

        Export Ledger

    </button>

</div>

    </div>

{/* SUMMARY */}

<div className="reconciliation-summary">

    {/* BANK BALANCE */}

    <div className="summary-card balance">

        <div className="summary-header">

            <div className="summary-icon">
                <CircleDollarSign size={22}/>
            </div>

            <span className="summary-chip">
                LIVE
            </span>

        </div>

        <span className="summary-title">
            Current Bank Balance
        </span>

        <div className="summary-value">

            <span className="currency">
                GYD
            </span>

            <span className="amount">

                <CountUp
                    end={bankBalance}
                    separator=","
                    decimals={2}
                />

            </span>

        </div>

        <p>
            Live ledger balance
        </p>

    </div>


    {/* DEPOSITED */}

    <div className="summary-card deposited">

        <div className="summary-header">

            <div className="summary-icon">
                <Wallet size={22}/>
            </div>

        </div>

        <span className="summary-title">
            Deposited
        </span>

        <div className="summary-value">

            <span className="currency">
                GYD
            </span>

            <span className="amount">

                <CountUp
                    end={depositedValue}
                    separator=","
                    decimals={2}
                />

            </span>

        </div>

        <p>
            Awaiting bank clearance
        </p>

    </div>


    {/* CLEARED */}

    <div className="summary-card cleared">

        <div className="summary-header">

            <div className="summary-icon">
                <CheckCircle2 size={22}/>
            </div>

        </div>

        <span className="summary-title">
            Cleared
        </span>

        <div className="summary-value">

            <span className="currency">
                GYD
            </span>

            <span className="amount">

                <CountUp
                    end={clearedValue}
                    separator=","
                    decimals={2}
                />

            </span>

        </div>

        <p>
            Successfully cleared
        </p>

    </div>


    {/* OUTSTANDING */}

    <div className="summary-card outstanding">

        <div className="summary-header">

            <div className="summary-icon">
                <Clock3 size={22}/>
            </div>

        </div>

        <span className="summary-title">
            Unpresented
        </span>

        <div className="summary-value">

            <span className="currency">
                GYD
            </span>

            <span className="amount">

                <CountUp
                    end={outstandingValue}
                    separator=","
                    decimals={2}
                />

            </span>

        </div>

        <p>
           Received but not yet presented
        </p>

    </div>


    {/* CANCELLED */}

    <div className="summary-card cancelled">

        <div className="summary-header">

            <div className="summary-icon">
                <XCircle size={22}/>
            </div>

        </div>

        <span className="summary-title">
            Cancelled
        </span>

        <div className="summary-value">

            <span className="currency">
                GYD
            </span>

            <span className="amount">

                <CountUp
                    end={cancelledValue}
                    separator=","
                    decimals={2}
                />

            </span>

        </div>

        <p>
           Removed from ledger
        </p>

    </div>


    {/* TRANSACTIONS */}

    <div className="summary-card transactions">

        <div className="summary-header">

            <div className="summary-icon">
                <Landmark size={22}/>
            </div>

        </div>

        <span className="summary-title">
           Transactions
        </span>

        <div className="summary-value transaction-value">

            <span className="amount">

                <CountUp
                    end={totalTransactions}
                />

            </span>

        </div>

        <p>
           Ledger entries
        </p>

    </div>

</div>

    {/* FILTERS */}

    <div className="toolbar">

        <div className="search-box">

            <Search size={18}/>

            <input

                placeholder="Search payer, payee or cheque..."

                value={search}

                onChange={(e)=>

                    setSearch(

                        e.target.value

                    )

                }

            />

        </div>



        <select

            value={statusFilter}

            onChange={(e)=>

                setStatusFilter(

                    e.target.value

                )

            }

        >

            <option value="ALL">

                All Statuses

            </option>

            <option value="Deposited">

                Deposited

            </option>

            <option value="Cleared">

                Cleared

            </option>

            <option value="Unpresented">

                Outstanding

            </option>

            <option value="Cancelled">

                Cancelled

            </option>

        </select>



        <input

            type="month"

            value={selectedMonth}

            onChange={(e)=>

                setSelectedMonth(

                    e.target.value

                )

            }

        />



<button

    className="refresh-btn"

    onClick={loadLedger}

>

    <RefreshCcw size={18}/>

    Refresh

</button>

    </div>



    {/* LEDGER */}

    <div className="ledger-card">

        <div className="ledger-header">

            <h2>

                Bank Ledger

            </h2>

        </div>



        <div className="ledger-table-wrapper">

            <table className="ledger-table">

                <thead>

<tr>

    <th>Date</th>

    <th>Entry #</th>

    <th>Source</th>

    <th>Category</th>

    <th>Type</th>

    <th>Description</th>

    <th>Reference</th>

    <th>Debit</th>

    <th>Credit</th>

    <th>Balance</th>

</tr>

                </thead>



                <tbody>

                    {

                        loading ?

                        (

                            <tr>

                                <td

                                    colSpan="9"

                                    className="empty-table"

                                >

                                    Loading...

                                </td>

                            </tr>

                        )

                        :

                        ledger.length===0 ?

                        (

                            <tr>

                                <td

                                   colSpan="9"

                                    className="empty-table"

                                >

                                    No ledger records found.

                                </td>

                            </tr>

                        )

                        :

ledger.map((row,index)=>(

<Fragment key={row.id ?? index}>

<tr

    className={

        row.source==="MANUAL"

        ?

        "expandable-row"

        :

        ""

    }

    onClick={()=>{

        if(row.source!=="MANUAL")
            return;

        setExpandedRow(

            expandedRow===row.id

            ?

            null

            :

            row.id

        );

    }}

>

    <td>

        {new Date(row.date).toLocaleDateString()}

    </td>

    <td>{row.entryNumber}</td>

    <td>

        <span className={`source-pill ${row.source.toLowerCase()}`}>

            {row.source}

        </span>

    </td>

    <td>

        <span className={`category-pill ${

            row.category
            ?.toLowerCase()
            .replace(/\s+/g,"-")
            .replace(/_/g,"-")

        }`}>

            {row.category}

        </span>

    </td>

    <td>

        <span className={`type-pill ${

            row.type
            ?.toLowerCase()
            .replace(/\s+/g,"-")
            .replace(/_/g,"-")

        }`}>

            {row.type}

        </span>

    </td>

    <td>{row.description}</td>

    <td>{row.reference || "-"}</td>

    <td>

        {

            row.debit>0

            ?

            `GYD ${row.debit.toLocaleString(undefined,{minimumFractionDigits:2})}`

            :

            "-"

        }

    </td>

    <td>

        {

            row.credit>0

            ?

            `GYD ${row.credit.toLocaleString(undefined,{minimumFractionDigits:2})}`

            :

            "-"

        }

    </td>

    <td>

        <strong>

            GYD {row.runningBalance.toLocaleString(undefined,{
                minimumFractionDigits:2
            })}

        </strong>

    </td>

</tr>

{

row.source==="MANUAL"

&&

expandedRow===row.id

&&

<tr className="ledger-expanded-row">

<td colSpan={10}>

<div className="ledger-expanded-card">

<div className="ledger-details-grid">

<div>

<strong>Entry Number</strong>

<p>{row.entryNumber}</p>

</div>

<div>

<strong>Category</strong>

<p>{row.category}</p>

</div>

<div>

<strong>Type</strong>

<p>{row.type}</p>

</div>

<div>

<strong>Reference</strong>

<p>{row.reference}</p>

</div>

<div>

<strong>External Reference</strong>

<p>{row.externalReference || "-"}</p>

</div>

<div>

<strong>Debit</strong>

<p>

{

row.debit>0

?

`GYD ${row.debit.toLocaleString(undefined,{
minimumFractionDigits:2
})}`

:

"-"

}

</p>

</div>

<div>

<strong>Credit</strong>

<p>

{

row.credit>0

?

`GYD ${row.credit.toLocaleString(undefined,{
minimumFractionDigits:2
})}`

:

"-"

}

</p>

</div>

<div>

<strong>Balance After Entry</strong>

<p>

GYD {

row.runningBalance.toLocaleString(undefined,{
minimumFractionDigits:2
})

}

</p>

</div>

</div>

{

row.notes?.trim()

&&

<div className="ledger-notes">

<h4>Notes</h4>

<p>{row.notes}</p>

</div>

}

<div className="ledger-actions">

<button

    className="edit-entry-btn"

onClick={(e)=>{

    e.stopPropagation();

    handleEditEntry(row);

}}

>

    Edit Entry

</button>

    <button
        className="delete-entry-btn"


        onClick={(e)=>{

    e.stopPropagation();

    handleDeleteEntry(row);

}}
    >

        Delete Entry

    </button>

</div>

</div>

</td>

</tr>

}

</Fragment>

))
                    }

                </tbody>

            </table>

        </div>

    </div>

<LedgerEntryModal

    open={showLedgerModal}

    editingEntry={editingEntry}

    onClose={()=>{

        setShowLedgerModal(false);

        setEditingEntry(null);

    }}

    currentBalance={bankBalance}

    refreshLedger={loadLedger}

/>



{

confirmModal.open &&

<div className="confirm-overlay">

    <div className="confirm-modal">

        {

        confirmModal.type==="edit"

        ?

        <CircleAlert size={58} className="warning-icon"/>

        :

        <Trash2 size={58} className="danger-icon"/>

        }

        <h2>

            {

            confirmModal.type==="edit"

            ?

            "Edit Transaction?"

            :

            "Delete Transaction?"

            }

        </h2>

        <p>

            {

            confirmModal.type==="edit"

            ?

            "You are about to edit this ledger transaction. Continue?"

            :

            "You are about to permanently delete this ledger transaction. This action cannot be undone."

            }

        </p>

        <div className="confirm-buttons">

            <button

                className="cancel-confirm"

                onClick={()=>{

                    setConfirmModal({

                        open:false,

                        type:null,

                        row:null

                    });

                }}

            >

                Cancel

            </button>

            <button

                className={

                    confirmModal.type==="edit"

                    ?

                    "confirm-edit"

                    :

                    "confirm-delete"

                }

                onClick={handleConfirm}

            >

                {

                confirmModal.type==="edit"

                ?

                "Confirm Edit"

                :

                "Delete Transaction"

                }

            </button>

        </div>

    </div>

</div>

}

</div>

);

}